/**
 * The support-case categories a visitor is actually offered — the SSOT for
 * BOTH surfaces that name them.
 *
 * `/account/support` renders these as the picker; `/contact` renders the same
 * list as prose, telling a signed-out visitor what support handles before they
 * commit to signing in. Those two lists were never allowed to disagree, and
 * before this module the only thing stopping them was that only one of them
 * existed. A page that describes a picker it does not share a definition with
 * is a page that goes stale the first time the picker changes.
 *
 * Every `slug` is checked against the generated `SupportCaseCategorySlug`
 * union — derived from `support.v1.SupportCaseCategory` with UNSPECIFIED
 * stripped — so a proto rename is a compile error here rather than a runtime
 * 400 at the BFF. The label keys stay literal (rather than being derived from
 * the slug by template) so the static i18n-key coverage gate can still see
 * them: the FloorLevelSelect pattern.
 */
import type { SupportCaseCategorySlug } from '@/shared/lib/types/conversations';

/**
 * The label key for a category, under the `support` message namespace.
 *
 * A union of literals rather than `` `category.${string}` ``: `useTranslations`
 * validates relative keys against the generated catalog, and a template type
 * would widen that check away.
 */
export type SupportCategoryLabelKey =
  | 'category.general'
  | 'category.billing'
  | 'category.technical'
  | 'category.account'
  | 'category.listing'
  | 'category.inquiry'
  | 'category.complaint'
  | 'category.other';

/**
 * The offered categories, in the order both surfaces present them.
 *
 * `satisfies` (not `:`) so the literal `slug`s survive as literal types — the
 * `Record<SupportCaseCategorySlug, …>` maps that consumers key off this array
 * are then exhaustive by construction.
 */
export const OFFERED_SUPPORT_CATEGORIES = [
  { slug: 'general', labelKey: 'category.general' },
  { slug: 'billing', labelKey: 'category.billing' },
  { slug: 'technical', labelKey: 'category.technical' },
  { slug: 'account', labelKey: 'category.account' },
  { slug: 'listing', labelKey: 'category.listing' },
  { slug: 'inquiry', labelKey: 'category.inquiry' },
  { slug: 'complaint', labelKey: 'category.complaint' },
  { slug: 'other', labelKey: 'category.other' },
] as const satisfies ReadonlyArray<{
  slug: SupportCaseCategorySlug;
  labelKey: SupportCategoryLabelKey;
}>;
